import React, { useCallback, useEffect, useState } from 'react';
import { getUiLocale } from '@/lib/dates';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, ClipboardCheck, IdCard, Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { clientApi } from '@/lib/adminApi';
import { formatApiError } from '@/lib/api';

// Sprint A6.2 — Evaluated-user wellbeing survey surface on /me.
// Lists active surveys for the caller, opens a submit dialog with Likert 1-5.

export default function MeSurveysCard() {
  const { t, i18n } = useTranslation();
  const isES = i18n.language?.startsWith('es');
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [profileInvite, setProfileInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // selected survey for submit dialog
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, p] = await Promise.all([
        clientApi.myWellbeingSurveys(),
        clientApi.myProfileCampaign().catch(() => null),
      ]);
      setItems(d?.items || []);
      setProfileInvite(p?.pending ? p.invite : null);
    } catch {
      setItems([]);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openSurvey = (s) => {
    setActive(s);
    setAnswers(Object.fromEntries((s.questions || []).map((q) => [q.id, 0])));
  };

  const submit = async () => {
    if (!active) return;
    const required = (active.questions || []).length;
    const provided = Object.values(answers).filter((v) => v >= 1 && v <= 5).length;
    if (provided !== required) {
      toast.error(isES ? 'Responde todas las preguntas.' : 'Please answer every question.');
      return;
    }
    setSubmitting(true);
    try {
      await clientApi.submitWellbeingResponse(active.id, answers);
      toast.success(isES ? 'Respuesta enviada' : 'Response submitted');
      setActive(null);
      setAnswers({});
      load();
    } catch (e) {
      toast.error(isES ? 'No se pudo enviar' : 'Submission failed', { description: formatApiError(e) });
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <Card className="p-6 shadow-card" data-testid="me-surveys-card">
        <div className="flex items-center text-sm text-muted-foreground">
          <Loader2 className="h-3 w-3 mr-2 animate-spin" />
          {isES ? 'Cargando encuestas…' : 'Loading surveys…'}
        </div>
      </Card>
    );
  }

  const profileCard = profileInvite ? (
    <Card className="p-6 shadow-card mb-4" data-testid="me-profile-invite-card">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-pink-500/15 text-pink-600 shrink-0">
          <IdCard className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <h2 className="font-semibold text-base">
            {isES ? 'Completa tu perfil personal' : 'Complete your personal profile'}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isES
              ? 'Tu empresa te invitó a compartir tus intereses, hobbies, preferencias y cumpleaños. Es voluntario y ayuda a celebrarte mejor.'
              : 'Your company invited you to share your interests, hobbies, preferences and birthday. It is voluntary and helps celebrate you better.'}
          </p>
          {profileInvite.deadline && (
            <p className="text-xs text-muted-foreground mt-1 inline-flex items-center gap-1">
              <CalendarClock className="h-3 w-3" />
              {isES ? 'Fecha límite' : 'Deadline'}: {new Date(`${profileInvite.deadline}T12:00:00`).toLocaleDateString(getUiLocale())}
            </p>
          )}
        </div>
        <Button size="sm" onClick={() => navigate('/me/profile')} data-testid="me-profile-invite-cta">
          {isES ? 'Completar' : 'Complete it'}
        </Button>
      </div>
    </Card>
  ) : null;

  if (items.length === 0) {
    return profileCard; // hide the surveys card entirely when nothing is pending
  }

  return (
    <>
    {profileCard}
    <Card className="p-6 shadow-card" data-testid="me-surveys-card">
      <div className="flex items-start gap-3 mb-4">
        <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-primary/15 text-primary shrink-0">
          <ClipboardCheck className="h-5 w-5" />
        </span>
        <div>
          <h2 className="font-semibold text-base">
            {isES ? 'Encuestas de bienestar' : 'Wellbeing surveys'}
          </h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            {isES ? `${items.length} pendiente${items.length === 1 ? '' : 's'}` : `${items.length} pending`}
          </p>
        </div>
      </div>
      <ul className="space-y-2" data-testid="me-surveys-list">
        {items.map((s) => (
          <li key={s.id} className="flex items-center justify-between gap-2 p-3 rounded-md border bg-card" data-testid={`me-survey-${s.id}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{isES ? s.title_es : s.title_en}</p>
              <p className="text-xs text-muted-foreground">
                {(s.questions || []).length} {isES ? 'preguntas' : 'questions'} · {s.anonymity === 'anonymous'
                  ? (isES ? 'Anónima' : 'Anonymous')
                  : (isES ? 'Identificada' : 'Named')}
                {s.deadline && (
                  <> · {isES ? 'antes del' : 'due'} {new Date(`${s.deadline}T12:00:00`).toLocaleDateString(getUiLocale())}</>
                )}
              </p>
            </div>
            <Button size="sm" onClick={() => openSurvey(s)} data-testid={`me-survey-${s.id}-take`}>
              {isES ? 'Responder' : 'Take it'}
            </Button>
          </li>
        ))}
      </ul>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="me-survey-dialog">
          {active && (
            <>
              <DialogHeader>
                <DialogTitle>{isES ? active.title_es : active.title_en}</DialogTitle>
                <DialogDescription>
                  {isES
                    ? `Escala 1 (totalmente en desacuerdo) – 5 (totalmente de acuerdo). ${active.anonymity === 'anonymous' ? 'Tus respuestas son anónimas.' : 'Tu respuesta queda asociada a tu perfil.'}`
                    : `Scale 1 (strongly disagree) – 5 (strongly agree). ${active.anonymity === 'anonymous' ? 'Your answers are anonymous.' : 'Your response is recorded against your profile.'}`}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-2">
                {(active.questions || []).map((q, idx) => (
                  <div key={q.id} className="space-y-2" data-testid={`me-survey-q-${q.id}`}>
                    <p className="text-sm font-medium">
                      <span className="text-muted-foreground mr-2">{idx + 1}.</span>
                      {isES ? q.text_es : q.text_en}
                    </p>
                    <div className="flex gap-2">
                      {[1, 2, 3, 4, 5].map((v) => {
                        const selected = answers[q.id] === v;
                        return (
                          <button
                            key={v}
                            type="button"
                            onClick={() => setAnswers((a) => ({ ...a, [q.id]: v }))}
                            data-testid={`me-survey-q-${q.id}-score-${v}`}
                            className={`flex-1 py-2 rounded-md border text-sm font-medium transition-colors ${
                              selected
                                ? 'bg-primary text-primary-foreground border-primary'
                                : 'bg-card hover:bg-muted/60'
                            }`}
                          >
                            {v}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setActive(null)} disabled={submitting} data-testid="me-survey-cancel">
                  {isES ? 'Cancelar' : 'Cancel'}
                </Button>
                <Button onClick={submit} disabled={submitting} data-testid="me-survey-submit">
                  {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                  {isES ? 'Enviar respuestas' : 'Submit response'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
    </>
  );
}
